import { Helmet } from "react-helmet-async";

interface SEOProps {
  // Core metadata
  title: string;
  description: string;
  
  // Article-specific
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags?: string[];
  section?: string;
  
  // Images
  image?: string;
  imageAlt?: string;
  
  // URLs
  url?: string;
  canonical?: string;
  
  // Advanced SEO
  keywords?: string[];
  readingTime?: number;
  wordCount?: number;
  
  // Social-specific overrides
  twitterTitle?: string;
  twitterDescription?: string;
  ogTitle?: string;
  ogDescription?: string;
  
  // Structured data
  noIndex?: boolean;
  noFollow?: boolean;
}

const SITE_NAME = "Bekah June";
const SITE_URL = "https://bekahjune.com";
const DEFAULT_IMAGE = `${SITE_URL}/desk.jpg`;
const DEFAULT_DESCRIPTION = "Engineer, founder, and writer building at the intersection of technology and human connection.";
const TWITTER_HANDLE = "@bekahjune"; // Update if you have a Twitter handle

export default function SEO({
  title,
  description,
  type = "website",
  publishedTime,
  modifiedTime,
  author = "Bekah June",
  tags = [],
  section = "Technology",
  image,
  imageAlt,
  url,
  canonical,
  keywords = [],
  readingTime,
  wordCount,
  twitterTitle,
  twitterDescription,
  ogTitle,
  ogDescription,
  noIndex = false,
  noFollow = false,
}: SEOProps) {
  // Ensure absolute URLs
  const absoluteImage = image 
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : DEFAULT_IMAGE;
  
  const absoluteUrl = url 
    ? (url.startsWith("http") ? url : `${SITE_URL}${url}`)
    : SITE_URL;
  
  const canonicalUrl = canonical 
    ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`)
    : absoluteUrl;

  // Build page title
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  
  // Merge tags into keywords
  const allKeywords = [...new Set([...keywords, ...tags])];
  
  // Social-specific fallbacks
  const ogTitleFinal = ogTitle || title;
  const ogDescriptionFinal = ogDescription || description;
  const twitterTitleFinal = twitterTitle || title;
  const twitterDescriptionFinal = twitterDescription || description;

  // Build robots directive
  const robotsDirectives: string[] = [];
  if (noIndex) robotsDirectives.push("noindex");
  else robotsDirectives.push("index");
  if (noFollow) robotsDirectives.push("nofollow");
  else robotsDirectives.push("follow");
  robotsDirectives.push("max-snippet:-1", "max-image-preview:large", "max-video-preview:-1");

  // JSON-LD Structured Data for Articles
  const articleStructuredData = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": absoluteImage,
    "author": {
      "@type": "Person",
      "name": author,
      "url": SITE_URL,
    },
    "publisher": {
      "@type": "Person",
      "name": SITE_NAME,
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_IMAGE,
      },
    },
    "url": absoluteUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": absoluteUrl,
    },
    ...(publishedTime && { "datePublished": publishedTime }),
    ...(modifiedTime && { "dateModified": modifiedTime }),
    ...(modifiedTime === undefined && publishedTime && { "dateModified": publishedTime }),
    ...(tags.length > 0 && { "keywords": tags.join(", ") }),
    ...(section && { "articleSection": section }),
    ...(wordCount && { "wordCount": wordCount }),
  } : null;

  // BreadcrumbList for articles
  const breadcrumbStructuredData = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Essays",
        "item": `${SITE_URL}/essays`,
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": title,
        "item": absoluteUrl,
      },
    ],
  } : null;

  // WebSite structured data for homepage
  const websiteStructuredData = type === "website" ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": DEFAULT_DESCRIPTION,
    "author": {
      "@type": "Person",
      "name": author,
    },
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      
      {/* Keywords (still useful for some search engines) */}
      {allKeywords.length > 0 && (
        <meta name="keywords" content={allKeywords.join(", ")} />
      )}
      
      {/* Author */}
      <meta name="author" content={author} />
      
      {/* Robots */}
      <meta name="robots" content={robotsDirectives.join(", ")} />
      <meta name="googlebot" content={robotsDirectives.join(", ")} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={absoluteUrl} />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={ogDescriptionFinal} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:secure_url" content={absoluteImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article-specific Open Graph */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" && tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:url" content={absoluteUrl} />
      <meta name="twitter:title" content={twitterTitleFinal} />
      <meta name="twitter:description" content={twitterDescriptionFinal} />
      <meta name="twitter:image" content={absoluteImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
      
      {/* Additional Twitter meta for articles */}
      {type === "article" && (
        <meta name="twitter:label1" content="Written by" />
      )}
      {type === "article" && (
        <meta name="twitter:data1" content={author} />
      )}
      {type === "article" && readingTime && (
        <meta name="twitter:label2" content="Est. reading time" />
      )}
      {type === "article" && readingTime && (
        <meta name="twitter:data2" content={`${readingTime} min read`} />
      )}
      
      {/* Additional SEO meta tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#FFFFFF" />
      
      {/* Article reading time for rich results */}
      {readingTime && (
        <meta name="reading-time" content={`${readingTime} minutes`} />
      )}
      
      {/* Structured Data */}
      {articleStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      )}
      {breadcrumbStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      )}
      {websiteStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(websiteStructuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Helper function to calculate reading time from word count
export function calculateReadingTime(wordCount: number): number {
  const wordsPerMinute = 200; // Average reading speed
  return Math.ceil(wordCount / wordsPerMinute);
}

// Helper function to estimate word count from content
export function estimateWordCount(content: string): number {
  // Remove markdown/MDX syntax and count words
  const plainText = content
    .replace(/```[\s\S]*?```/g, "") // Remove code blocks
    .replace(/`[^`]*`/g, "") // Remove inline code
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // Convert links to text
    .replace(/[#*_~]/g, "") // Remove markdown formatting
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .replace(/---[\s\S]*?---/g, "") // Remove frontmatter
    .trim();
  
  const words = plainText.split(/\s+/).filter(word => word.length > 0);
  return words.length;
}

// Helper to generate SEO-friendly slug
export function generateSEOSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Remove consecutive hyphens
    .trim();
}

// Helper to truncate description to optimal length
export function truncateDescription(text: string, maxLength: number = 160): string {
  if (text.length <= maxLength) return text;
  
  // Try to cut at a sentence boundary
  const truncated = text.substring(0, maxLength);
  const lastSentence = truncated.lastIndexOf(". ");
  
  if (lastSentence > maxLength * 0.6) {
    return truncated.substring(0, lastSentence + 1);
  }
  
  // Fall back to word boundary
  const lastSpace = truncated.lastIndexOf(" ");
  return truncated.substring(0, lastSpace) + "...";
}
